import React from "react";
import { Link } from "react-router-dom";
import * as Yup from "yup";
import { TranslatedText } from "../../../helpers/helpers";

import {
  CaseForms,
  CaseMutationType,
  CaseStepperType,
} from "../../../helpers/enums";
import { validEmail, validPhone } from "../../forms/CustomSignUp/validations";

import {
  getEvictionForMutation,
  READING_ACTIVATION,
} from "../../../helpers/mutations";
import PrivacyPolicy from "../../common/PrivacyPolicy/PrivacyPolicy";
import CustomLetturaSicuraStep from "../custom-steps/CustomTutelaPerditaInitialStep/CustomLetturaSicuraStep";

// Il model definisce la struttura dei dati
// formId è un identificatore del form
// formField contiene la definizione dei singoli campi
const model = {
  formId: CaseForms.LetturaSicuraAttiva,
  formField: {
    name: {
      name: "name",
      label: (
        <TranslatedText i18nKey="cases.lettura_sicura.attiva.nome.label" />
      ),
      placeholder: (
        <TranslatedText i18nKey="cases.lettura_sicura.attiva.nome.placeholder" />
      ),
      requiredErrorMsg: <TranslatedText i18nKey="cases.errors.required" />,
      readonly: true,
      type: "input",
    },
    surname: {
      name: "surname",
      label: (
        <TranslatedText i18nKey="cases.lettura_sicura.attiva.cognome.label" />
      ),
      placeholder: (
        <TranslatedText i18nKey="cases.lettura_sicura.attiva.cognome.placeholder" />
      ),
      requiredErrorMsg: <TranslatedText i18nKey="cases.errors.required" />,
      readonly: true,
      type: "input",
    },
    email: {
      name: "email",
      label: (
        <TranslatedText i18nKey="cases.lettura_sicura.attiva.email.label" />
      ),
      placeholder: (
        <TranslatedText i18nKey="cases.lettura_sicura.attiva.email.placeholder" />
      ),
      requiredErrorMsg: <TranslatedText i18nKey="cases.errors.required" />,
      invalidErrorMsg: <TranslatedText i18nKey="cases.errors.email_invalid" />,
      readonly: true,
      type: "input",
    },
    mobilePhoneNumber: {
      name: "mobilePhoneNumber",
      label: (
        <TranslatedText i18nKey="cases.lettura_sicura.attiva.cellulare.label" />
      ),
      placeholder: (
        <TranslatedText i18nKey="cases.lettura_sicura.attiva.cellulare.placeholder" />
      ),
      requiredErrorMsg: <TranslatedText i18nKey="cases.errors.required" />,
      invalidErrorMsg: <TranslatedText i18nKey="cases.errors.phone_invalid" />,
      readonly: true,
      type: "input",
    },
    // codiceCliente: {
    //   name: "codiceCliente",
    //   label: (
    //     <TranslatedText i18nKey="cases.lettura_sicura.attiva.codiceCliente.label" />
    //   ),
    //   placeholder: (
    //     <TranslatedText i18nKey="cases.lettura_sicura.attiva.codiceCliente.placeholder" />
    //   ),
    //   requiredErrorMsg: <TranslatedText i18nKey="cases.errors.required" />,
    //   type: "input",
    // },
    contractId: {
      name: "contractId",
      label: (
        <TranslatedText i18nKey="cases.lettura_sicura.attiva.codiceContratto.label" />
      ),
      placeholder: (
        <TranslatedText i18nKey="cases.lettura_sicura.attiva.codiceContratto.placeholder" />
      ),
      requiredErrorMsg: <TranslatedText i18nKey="cases.errors.required" />,
      type: "input",
    },
  },
};

const {
  formField: {
    name,
    surname,
    email,
    mobilePhoneNumber,
    // codiceCliente,
    contractId,
  },
} = model;

// initialValues contiene i valori iniziali del form, ne imposta anche i campi.
// Si può usare per popolare il form eventualmente.
const initialValues = {
  [name.name]: "",
  [surname.name]: "",
  [email.name]: "",
  [mobilePhoneNumber.name]: "",
  //[codiceCliente.name]: "",
  [contractId.name]: "",
};

// Per definire il validationSchema si usa un array di shape che indicano gli step.
// L'ordine degli step deve corrispondere a quello in layout.

const getValidationSchema = () => {
  //const validationFiscalCode = getValidationRule("fiscalCode");
  // console.log(validationFiscalCode);
  return [
    Yup.object().shape({
      [name.name]: Yup.string().required(name.requiredErrorMsg),
      [surname.name]: Yup.string().required(surname.requiredErrorMsg),
      [email.name]: Yup.string()
        .required(email.requiredErrorMsg)
        .matches(validEmail, {
          message: email.invalidErrorMsg,
        }),
      [mobilePhoneNumber.name]: Yup.string()
        .required(mobilePhoneNumber.requiredErrorMsg)
        .matches(validPhone, {
          message: mobilePhoneNumber.invalidErrorMsg,
        }),
      // [codiceCliente.name]: Yup.string().required(
      //   codiceCliente.requiredErrorMsg
      // ),
      [contractId.name]: Yup.string().required(contractId.requiredErrorMsg),
    }),
  ];
};

// Il layout contiene informazioni di testo e di distribuzione sulle griglie
const getLayout = (
  inWebView,
  webviewRedirectPopup,
  privacyLink,
  privacyCtaLabel
) => {
  const userUrl = "/user-profile";
  const userMobileAnchor = "gruppocap://view/native/myUserData";

  return [
    {
      chapter: true,
      chapterName: "Lettura sicura",
      title: "",
      component: CustomLetturaSicuraStep,
      fields: [
        {
          fieldName: name.name,
          grid: {
            xs: 12,
            md: 6,
          },
        },
        {
          fieldName: surname.name,
          grid: {
            xs: 12,
            md: 6,
          },
        },
        {
          fieldName: email.name,
          grid: {
            xs: 12,
            md: 6,
          },
        },
        {
          fieldName: mobilePhoneNumber.name,
          grid: {
            xs: 12,
            md: 6,
          },
        },
        {
          fieldName: null,
          text: (
            <div className="info-label">
              <span className="gc-icon-functional-attention-2-col">
                <span className="path1"></span>
                <span className="path2"></span>
              </span>
              <p>
                {<TranslatedText i18nKey="cases.forms.user_profile.part1" />}{" "}
                {inWebView ? (
                  <span
                    className={"url-link"}
                    onClick={() =>
                      webviewRedirectPopup(
                        "webview.header.title.attention",
                        "webview.header.message.navigate-away",
                        "cases.buttons.continue",
                        "showPopup",
                        userMobileAnchor,
                        "common.close"
                      )
                    }
                  >
                    <TranslatedText i18nKey="cases.forms.user_profile.part2" />
                  </span>
                ) : (
                  <Link to={userUrl}>
                    <u>
                      {
                        <TranslatedText i18nKey="cases.forms.user_profile.part2" />
                      }
                    </u>
                  </Link>
                )}{" "}
                {<TranslatedText i18nKey="cases.forms.user_profile.part3" />}
              </p>
            </div>
          ),
          grid: {
            xs: 12,
          },
        },
      ],

      content: (
        <div>
          <p>
            <PrivacyPolicy
              privacyLink={privacyLink}
              privacyCtaLabel={privacyCtaLabel}
            />
          </p>
        </div>
      ),
    },
  ];
};

// Contiene dettagli sul case form
const formDetails = {
  title: <TranslatedText i18nKey="cases.lettura_sicura.title" />,
  description: null,
  summary: false,
  stepper: CaseStepperType.None,
  formOnly: true,
  backTo: "/",
  backToText: <TranslatedText i18nKey="common.back.home" />,
  blockIfNoContracts: true,
  helpText: null,
  blockText: (
    <div className="content">
      <p>
        <TranslatedText i18nKey="cases.modal.contracts.block_text" />
      </p>
    </div>
  ),
  successText: (
    <div>
      <p>
        <TranslatedText i18nKey="cases.request_info.success_text" />
      </p>
    </div>
  ),
};

const mutation = {
  type: CaseMutationType.Custom,
  mutation: READING_ACTIVATION,
  refetchQueries: getEvictionForMutation("safeReadingActivation"),
};
const getTrackingEventKo = () => () => {
  window.dataLayer.push({
    event: "pratica",
    tipo: CaseForms.LetturaSicuraAttiva,
    step: "invio_richiesta",
    esito: "KO",
  });
};
const getTrackingEvent = () => () => {
  window.dataLayer.push({
    event: "pratica",
    tipo: CaseForms.LetturaSicuraAttiva,
    step: "invio_richiesta",
    esito: "OK",
  });
};
const main = (
  excludeSteps,
  counter,
  signDigital,
  webviewPopup,
  webviewRedirectPopup,
  buttonMatricolaContatoreCase,
  privacyLink,
  privacyCtaLabel
) => {
  const inWebView = !!webviewRedirectPopup;
  return {
    model,
    trackingEventKo: getTrackingEventKo(),
    trackingEvent: getTrackingEvent(),
    validationSchema: getValidationSchema(),
    initialValues,
    formDetails,
    layout: getLayout(
      inWebView,
      webviewRedirectPopup,
      privacyLink,
      privacyCtaLabel
    ),
    mutation,
  };
};

export default main;
